import { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Download, Factory, Zap, MapPin, User, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function PowerPlantReport({ user, onLogout }) {
  const [powerPlant, setPowerPlant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Campos para o relatório
  const [referenceMonth, setReferenceMonth] = useState("");
  const [distributorInvoiceValue, setDistributorInvoiceValue] = useState("");
  const [generatedEnergy, setGeneratedEnergy] = useState("");
  const [fioBValue, setFioBValue] = useState("");

  useEffect(() => {
    fetchMyPowerPlant();
  }, []);

  const fetchMyPowerPlant = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/powerplants/my-powerplant");
      setPowerPlant(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar dados da usina");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!referenceMonth) {
      toast.error("Informe o mês de referência");
      return;
    }
    if (!distributorInvoiceValue || !generatedEnergy || !fioBValue) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    try {
      setGenerating(true);
      
      const reportData = {
        power_plant_id: powerPlant.id,
        reference_month: referenceMonth,
        distributor_invoice_value: parseFloat(distributorInvoiceValue.replace(',', '.')),
        generated_energy: parseFloat(generatedEnergy.replace(',', '.')),
        fio_b_value: parseFloat(fioBValue.replace(',', '.'))
      };

      const response = await axiosInstance.post(
        '/powerplants-billing/report/detailed-pdf',
        reportData,
        { responseType: 'blob' }
      );

      // Create download link with correct content type
      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const plantName = powerPlant?.plant_name || 'usina';
      const filename = `Relatorio_Medicao_${plantName.replace(/\s+/g, '_')}_${referenceMonth.replace('/', '_')}.pdf`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Relatório gerado com sucesso!");
      
      // Limpar campos após geração
      setReferenceMonth("");
      setDistributorInvoiceValue("");
      setGeneratedEnergy("");
      setFioBValue("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar relatório");
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando dados da usina...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!powerPlant) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Factory className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Nenhuma usina encontrada</h2>
            <p className="text-gray-500">Não foi possível encontrar uma usina associada ao seu usuário.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Relatório de Medição</h1>
          <p className="text-gray-600">Gere o relatório de medição da sua usina</p>
        </div>

        {/* Informações da Usina */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Factory className="w-5 h-5" />
              {powerPlant.plant_name}
            </CardTitle>
            <CardDescription className="text-green-100">
              Dados da sua usina cadastrada
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Proprietário */}
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Proprietário</p>
                  <p className="font-medium">{powerPlant.owner_name}</p>
                </div>
              </div>

              {/* Distribuidora */}
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-yellow-500 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Distribuidora</p>
                  <p className="font-medium">{powerPlant.distributor || "-"}</p>
                </div>
              </div>

              {/* Número de Instalação */}
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-500 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Nº Instalação Gerador</p>
                  <p className="font-medium">{powerPlant.generator_installation_number || "-"}</p>
                </div>
              </div>

              {/* Potência Instalada */}
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-orange-500 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Potência Instalada</p>
                  <p className="font-medium">{powerPlant.installed_power ? `${powerPlant.installed_power} kW` : "-"}</p>
                </div>
              </div>

              {/* Data de Operação */}
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-purple-500 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Data de Operação</p>
                  <p className="font-medium">{formatDate(powerPlant.operation_date)}</p>
                </div>
              </div>

              {/* Localização */}
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-red-500 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Localização</p>
                  <p className="font-medium">
                    {powerPlant.plant_city && powerPlant.plant_state 
                      ? `${powerPlant.plant_city} - ${powerPlant.plant_state}`
                      : "-"
                    }
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Formulário de Geração de Relatório */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Gerar Relatório de Medição
            </CardTitle>
            <CardDescription>
              Preencha os dados abaixo para gerar o relatório em PDF
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Mês de Referência */}
              <div className="space-y-2">
                <Label htmlFor="referenceMonth">Mês de Referência *</Label>
                <Input
                  id="referenceMonth"
                  data-testid="reference-month-input"
                  placeholder="Ex: JAN/2026"
                  value={referenceMonth}
                  onChange={(e) => setReferenceMonth(e.target.value.toUpperCase())}
                />
                <p className="text-xs text-gray-500">Formato: MÊS/ANO (ex: JAN/2026)</p>
              </div>

              {/* Valor Fatura Distribuidora */}
              <div className="space-y-2">
                <Label htmlFor="distributorInvoiceValue">Valor Fatura da Distribuidora (R$) *</Label>
                <Input
                  id="distributorInvoiceValue"
                  data-testid="distributor-invoice-input"
                  placeholder="Ex: 1500,00"
                  value={distributorInvoiceValue}
                  onChange={(e) => setDistributorInvoiceValue(e.target.value)}
                />
              </div>

              {/* Energia Gerada */}
              <div className="space-y-2">
                <Label htmlFor="generatedEnergy">Energia Gerada (kWh) *</Label>
                <Input
                  id="generatedEnergy"
                  data-testid="generated-energy-input"
                  placeholder="Ex: 5000"
                  value={generatedEnergy}
                  onChange={(e) => setGeneratedEnergy(e.target.value)}
                />
              </div>

              {/* Valor Fio B */}
              <div className="space-y-2">
                <Label htmlFor="fioBValue">Valor do Fio B (R$) *</Label>
                <Input
                  id="fioBValue"
                  data-testid="fio-b-input"
                  placeholder="Ex: 200,00"
                  value={fioBValue}
                  onChange={(e) => setFioBValue(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-6">
              <Button
                data-testid="generate-report-btn"
                onClick={handleGenerateReport}
                disabled={generating}
                className="w-full md:w-auto bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Gerando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Gerar Relatório PDF
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
